import React from 'react'
import { Typography, withStyles } from '@material-ui/core'
import { FormatQuote, Cancel, ArrowLeft } from '@material-ui/icons'
import Field from 'components/AddOrEditFormPage/CustomForm/Field'
import { sections } from 'components/AddOrEditFormPage/Form/Sections'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
	FormatListChecks,
	FormatListCheckbox,
	FormatListBulleted,
	Numeric,
} from 'mdi-material-ui'
import {
	changeSection,
	editSection,
	selectSection,
	handleFormValueChange,
	deleteSection,
} from 'actions/customForm/addOrEdit'
import { nanoid } from 'nanoid'
import { getCustomAttributes } from 'actions/contacts'
import { startCase, lowerCase } from 'lodash'
import Button from 'components/theme/Button'

const styles = (theme) => ({
	selectMenu: {
		minHeight: 0,
		height: 'auto',
		padding: '5px 14px',
	},
	select: {
		'&:focus': {
			borderRadius: 35,
		},
	},
	outlinedInputRoot: {
		'&$focused $notchedOutline': {
			borderColor: '#014c94',
			borderWidth: 2,
		},
		'& $notchedOutline': {
			borderRadius: 28,
			paddingLeft: '40px !important',
		},
	},
	notchedOutline: {},
	focused: {},
})

const blank = ({
	isEditing,
	classes,
	isDragging,
	item,
	sectionTypeChosen,
	index,
	changeSection,
	editSection,
	selectSection,
	handleFormValueChange,
	customAttributesLoadStatus,
	customAttributes,
	getCustomAttributes,
	contactFieldsInUse,
	deleteSection,
}) => {
	if (!isEditing) {
		deleteSection(index)
	}

	if (!customAttributesLoadStatus) {
		getCustomAttributes()
	}

	const tooltip = (value) =>
		contactFieldsInUse[value]
			? 'This contact field is already in use'
			: undefined

	const defaultSections = [
		{
			value: 'email',
			label: 'Email',
			tooltip: tooltip('email'),
			disabled: Boolean(tooltip('email')),
		},
		{
			value: 'phone',
			label: 'Phone',
			tooltip: tooltip('phone'),
			disabled: Boolean(tooltip('phone')),
		},
		{
			value: 'first_name',
			label: 'First name',
			tooltip: tooltip('first_name'),
			disabled: Boolean(tooltip('first_name')),
		},
		{
			value: 'middle_name',
			label: 'Middle name',
			tooltip: tooltip('middle_name'),
			disabled: Boolean(tooltip('middle_name')),
		},
		{
			value: 'last_name',
			label: 'Last name',
			tooltip: tooltip('last_name'),
			disabled: Boolean(tooltip('last_name')),
		},
		{
			value: 'location',
			label: 'Location',
			tooltip: tooltip('location'),
			disabled: Boolean(tooltip('location')),
		},
	]

	const customSections = customAttributesLoadStatus
		? Object.values(customAttributes)
				.filter((customAttribute) => customAttribute.name)
				.map((customAttribute) => ({
					value: customAttribute.name,
					label: startCase(customAttribute.name),
					tooltip: tooltip(customAttribute.name),
					disabled: Boolean(tooltip(customAttribute.name)),
				}))
		: []

	const contactFieldSections = [...defaultSections, ...customSections]

	return (
		<>
			<div style={{ paddingTop: 10, paddingBottom: 10 }}>
				{sectionTypeChosen === 'blank' && (
					<>
						<Typography variant="h5">
							What should we add in this section?
						</Typography>
						<div onClick={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
							<Field
								items={[
									{ value: 'blank', label: 'Select Section Type' },
									...sections,
								]}
								label="Section type"
								select
								property="sectionTypeChosen"
								statePath={(state) =>
									state.customForm.addOrEdit.sectionTypeChosen
								}
								handleChange={({ property, value }) => {
									const blankTypes = {
										pagebreak: { type: value },
										separator: { type: value },
										htmlcontent: { type: value, htmlcontent: { HtmlPart: '' } },
										image: { type: value, label: '', url: '' },
										title: { type: value, label: '' },
									}

									const typeFuncs = {
										pagebreak: () => {
											changeSection(blankTypes[value], index, item.id).then(
												() => {
													editSection(undefined)
													selectSection(null)
												},
											)
										},
										separator: () => {
											changeSection(blankTypes[value], index, item.id).then(
												() => {
													editSection(undefined)
													selectSection(null)
												},
											)
										},
										htmlcontent: () => {
											changeSection(blankTypes[value], index, item.id)
										},
										image: () => {
											changeSection(blankTypes[value], index, item.id)
										},
										title: () => {
											changeSection(blankTypes[value], index, item.id)
										},
										blank: () => {
											return
										},
									}

									if (
										value !== 'multiple' &&
										value !== 'open' &&
										value !== 'contactfield'
									) {
										typeFuncs[value]()
										// changeSection(blankTypes[value], index)
									} else {
										handleFormValueChange(property, value)
									}
								}}
							/>
						</div>
					</>
				)}
				{sectionTypeChosen === 'multiple' && (
					<>
						<Typography variant="h5">
							What type of multiple choice question should we add?
						</Typography>
						<div onClick={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
							<Field
								items={[
									{ value: 'blank', label: 'Select Multiple choice type' },
									{ value: 'bool', label: 'Yes/no', Icon: FormatListChecks },
									{
										value: 'select',
										label: 'Choose one of a few options',
										Icon: FormatListBulleted,
									},
									{
										value: 'multi',
										label: 'Choose multiple options',
										Icon: FormatListCheckbox,
									},
								]}
								label="Multiple choice type"
								select
								value="blank"
								handleChange={({ property, value }) => {
									const blankTypes = {
                    bool: {
                      type: 'field',
                      field: {
                        type: value,
                        boolean_labels: {
                          false_label: 'no',
                          true_label: 'yes',
                        },
                        field_options: ['yes', 'no'],
                        field_name: item?.id || nanoid(),
                        required: false,
                        hidden: false,
                        readonly: false,
                      },
                    },
                    select: {
                      type: 'field',
                      field: {
                        type: value,
                        field_name: item?.id || nanoid(),
                        required: false,
                        hidden: false,
                        readonly: false,
                        options_allow_other: false,
                      },
                    },
                    multi: {
                      type: 'field',
                      field: {
                        type: value,
                        field_name: item?.id || nanoid(),
                        required: false,
                        hidden: false,
                        readonly: false,
                        options_allow_other: false,
                      },
                    },
                  }

									if (value !== 'blank') {
										changeSection(blankTypes?.[value], index, item?.id)
									}
								}}
							/>
						</div>
						<div style={{ textAlign: 'center', marginBottom: 5 }}>
							<Button
								variant="outlined"
								onClick={() =>
									handleFormValueChange('sectionTypeChosen', 'blank')
								}
							>
								<ArrowLeft style={{ marginRight: 5 }} />
								Go back
							</Button>
						</div>
					</>
				)}
				{sectionTypeChosen === 'open' && (
					<>
						<Typography variant="h5">
							What type of open-ended question should we add?
						</Typography>
						<div onClick={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
							<Field
								items={[
									{ value: 'blank', label: 'Select open-ended type' },
									{
										value: 'text',
										label: 'Text',
										Icon: FormatQuote,
									},
									{
										value: 'number',
										label: 'Numeric',
										Icon: Numeric,
									},
								]}
								label="Open-ended type"
								select
								value="blank"
								handleChange={({ property, value, id }) => {
									const blankTypes = {
										text: {
											type: 'field',
											field: {
												type: value,
												field_name: item?.id || nanoid(),
												required: false,
												hidden: false,
												readonly: false,
												field_options: [],
											},
										},
										number: {
											type: 'field',
											field: {
												type: value,
												field_name: item?.id || nanoid(),
												required: false,
												hidden: false,
												readonly: false,
												field_options: [],
												number_options: {
													decimal_places: 0,
												},
											},
										},
									}

									if (value !== 'blank') {
										changeSection(blankTypes?.[value], index, item?.id)
									}
								}}
							/>
						</div>
						<div style={{ textAlign: 'center', marginBottom: 5 }}>
							<Button
								variant="outlined"
								onClick={() =>
									handleFormValueChange('sectionTypeChosen', 'blank')
								}
							>
								<ArrowLeft style={{ marginRight: 5 }} />
								Go back
							</Button>
						</div>
					</>
				)}
				{sectionTypeChosen === 'contactfield' && (
					<>
						<Typography variant="h5">
							What type of contact field should we add?
						</Typography>
						<div onClick={(e) => e.stopPropagation()} style={{ marginTop: 10 }}>
							<Field
								items={[
									{ value: 'blank', label: 'Select a contact field' },
									...contactFieldSections,
								]}
								label="Contact field type"
								select
								value="blank"
								handleChange={({ property, value, id }) => {
									if (value !== 'blank') {
										changeSection(
                      {
                        type: 'contactfield',
                        label: startCase(value),
                        description: `Enter your ${lowerCase(value)}`,
                        contactfield: {
                          field_name: value,
                          required: false,
                          hidden: false,
                          readonly: false,
                          type: 'text',
                          field_options: [],
                        },
                      },
                      index,
                      item?.id,
                    )
									}
								}}
							/>
						</div>
						<div style={{ textAlign: 'center', marginBottom: 5 }}>
							<Button
								variant="outlined"
								onClick={() =>
									handleFormValueChange('sectionTypeChosen', 'blank')
								}
							>
								<ArrowLeft style={{ marginRight: 5 }} />
								Go back
							</Button>
						</div>
					</>
				)}
				<div style={{ textAlign: 'center' }}>
					<Button onClick={() => deleteSection(index)} variant="outlined">
						<Cancel style={{ marginRight: 5 }} />
						Cancel
					</Button>
				</div>
			</div>
		</>
	)
}

export default connect(
	(state) => ({
		sectionTypeChosen: state.customForm.addOrEdit.sectionTypeChosen,
		customAttributesLoadStatus: state.loadStatus.customAttributes,
		customAttributes: state.contacts.customAttributes,
		contactFieldsInUse: state.customForm.addOrEdit.contactFieldsInUse,
	}),
	(dispatch) =>
		bindActionCreators(
			{
				changeSection,
				editSection,
				selectSection,
				handleFormValueChange,
				getCustomAttributes,
				deleteSection,
			},
			dispatch,
		),
)(withStyles(styles)(blank))
